/**
 * Selection Highlight - CodeMirror 6 遠端選取高亮擴展
 *
 * 顯示其他協作者的文字選取範圍
 *
 * @module @linkode/web/editor/cm/selectionHighlight
 * @see SPEC-007 Requirements 6.3
 */

import { Extension, StateField, StateEffect, RangeSet } from '@codemirror/state';
import { Decoration, DecorationSet, EditorView } from '@codemirror/view';
import type { Selection, Position } from '@linkode/shared';
import { PRESENCE_COLORS } from './remoteCursor';

// ============================================================================
// Types
// ============================================================================

/**
 * Remote selection data for a single participant
 */
export interface RemoteSelectionData {
  /** Unique user ID */
  userId: string;
  /** Selection range (anchor/head), null if no selection */
  selection: Selection | null;
  /** Color ID (0-7) for visual distinction */
  colorId: number;
}

// ============================================================================
// Position Conversion Utilities
// ============================================================================

/**
 * Convert line/column Position to CodeMirror offset
 *
 * @param doc - Document object with line access
 * @param position - Position with line and column (0-indexed)
 * @returns Character offset in document, or null if position is invalid
 */
function positionToOffsetFromDoc(
  doc: { lines: number; line: (n: number) => { from: number; length: number } },
  position: Position
): number | null {
  const { line, column } = position;
  const lineCount = doc.lines;

  // Validate line number (0-indexed input, but doc.line expects 1-indexed)
  if (line < 0 || line >= lineCount) {
    return null;
  }

  const lineInfo = doc.line(line + 1); // Convert to 1-indexed

  // Clamp column to line length
  const clampedColumn = Math.min(Math.max(0, column), lineInfo.length);

  return lineInfo.from + clampedColumn;
}

// ============================================================================
// State Effects and Field
// ============================================================================

/**
 * Effect to update remote selections
 */
export const setRemoteSelections = StateEffect.define<RemoteSelectionData[]>();

/**
 * State field that tracks remote selection decorations
 */
const remoteSelectionField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },

  update(decorations, tr) {
    // Check for selection update effects
    for (const effect of tr.effects) {
      if (effect.is(setRemoteSelections)) {
        return buildSelectionDecorations(tr.state.doc, effect.value);
      }
    }

    // Map decorations through document changes
    if (tr.docChanged) {
      decorations = decorations.map(tr.changes);
    }

    return decorations;
  },

  provide: (field) => EditorView.decorations.from(field),
});

/**
 * Build decoration set from remote selection data
 *
 * @param doc - Document object
 * @param selections - Array of remote selection data
 * @returns DecorationSet with selection highlights
 */
function buildSelectionDecorations(
  doc: { lines: number; line: (n: number) => { from: number; length: number } },
  selections: RemoteSelectionData[]
): DecorationSet {
  const decorations: { from: number; to: number; decoration: Decoration }[] = [];

  for (const sel of selections) {
    if (!sel.selection) continue;

    const { anchor, head } = sel.selection;

    // Convert positions to offsets
    const anchorOffset = positionToOffsetFromDoc(doc, anchor);
    const headOffset = positionToOffsetFromDoc(doc, head);

    // Skip if either position is invalid
    if (anchorOffset === null || headOffset === null) continue;

    // Skip if no actual selection (anchor === head)
    if (anchorOffset === headOffset) continue;

    // Determine from/to (handle reverse selections)
    const from = Math.min(anchorOffset, headOffset);
    const to = Math.max(anchorOffset, headOffset);

    // Get color with alpha for background
    const color = PRESENCE_COLORS[sel.colorId % PRESENCE_COLORS.length];

    decorations.push({
      from,
      to,
      decoration: Decoration.mark({
        class: `cm-remote-selection cm-remote-selection-${sel.colorId}`,
        attributes: {
          'data-user-id': sel.userId,
          style: `background-color: ${hexToRgba(color ?? PRESENCE_COLORS[0], 0.3)};`,
        },
      }),
    });
  }

  // Sort by position (required by RangeSet)
  decorations.sort((a, b) => a.from - b.from || a.to - b.to);

  return RangeSet.of(decorations.map((d) => d.decoration.range(d.from, d.to)));
}

/**
 * Convert hex color to rgba with alpha
 *
 * @param hex - Hex color string (e.g., '#ef4444')
 * @param alpha - Alpha value (0-1)
 * @returns RGBA color string
 */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ============================================================================
// Styles
// ============================================================================

/**
 * Remote selection styles
 */
const remoteSelectionStyles = EditorView.baseTheme({
  '.cm-remote-selection': {
    borderRadius: '2px',
  },
});

// ============================================================================
// Extension Factory
// ============================================================================

/**
 * Create Selection Highlight extension for CodeMirror
 *
 * @returns CodeMirror extension for remote selection highlights
 *
 * @example
 * ```typescript
 * import { createSelectionHighlightExtension, setRemoteSelections } from './selectionHighlight';
 *
 * // Add extension to editor
 * const extensions = [
 *   createSelectionHighlightExtension(),
 *   // ... other extensions
 * ];
 *
 * // Update remote selections
 * view.dispatch({
 *   effects: setRemoteSelections.of([
 *     {
 *       userId: 'user1',
 *       selection: {
 *         anchor: { line: 0, column: 5 },
 *         head: { line: 0, column: 15 }
 *       },
 *       colorId: 0
 *     },
 *   ])
 * });
 * ```
 */
export function createSelectionHighlightExtension(): Extension {
  return [remoteSelectionField, remoteSelectionStyles];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Update remote selections in an EditorView
 *
 * @param view - EditorView instance
 * @param selections - Array of remote selection data
 */
export function updateRemoteSelections(view: EditorView, selections: RemoteSelectionData[]): void {
  view.dispatch({
    effects: setRemoteSelections.of(selections),
  });
}

export default createSelectionHighlightExtension;
